import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { toast } from 'vue3-toastify';

import { updateSocketToken } from '@/plugins/socket';
import { useAuthStore } from '@/stores/agency/auth';
import { useCommonStore } from '@/stores/agency/common';

// The API rotates the refresh token on every refresh, and tabs share it through localStorage, so
// only one tab may refresh at a time. Web Locks serialise this across all tabs of the origin.
const SESSION_LOCK_NAME = 'careezy-session';

// Treat the access token as expired slightly early to absorb clock skew and request latency.
const ACCESS_TOKEN_EXPIRY_SKEW_SECONDS = 30;

// One in-flight refresh per tab; every request that hits 401 awaits the same promise.
let refreshPromise: Promise<string> | null = null;

type RetriableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

/** Thrown when there is no refresh token to refresh with — the session cannot be recovered. */
class SessionExpiredError extends Error {}

// ✅ Create axios instance
const axiosIns = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    Accept: '*/*',
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*'
  }
});

// ℹ️ Add request interceptor to send the authorization header on each subsequent request after login
axiosIns.interceptors.request.use(config => {
  const token = localStorage.getItem('accessToken');
  const tenantSlug = localStorage.getItem('tenantSlug');
  const userRole = localStorage.getItem('userRole');
  const systemType = 'web';
  const commonStore = useCommonStore();

  // Get request headers and if headers is undefined assign blank object
  config.headers = config.headers || {};
  config.headers.platform = systemType;
  config.headers.appname = import.meta.env.VITE_APP_NAME;

  // If token and tenantSlug is found
  if (token && tenantSlug) {
    config.headers.Authorization = token ? `Bearer ${token}` : '';

    // config.headers['x-tenant-slug'] = JSON.parse(tenantSlug);
    config.headers['x-tenant-slug'] = tenantSlug;
  }

  if (userRole) config.headers['x-app-role'] = userRole.toLowerCase();

  // Api call loader
  if (!config?.headers?.['x-hide-loader'] || config?.headers?.['x-hide-loader'] === 'false')
    commonStore.setLoading(true);

  // Return modified config
  return config;
});

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Runs `task` while holding the cross-tab session lock, so token rotation and session validation
 * never overlap between tabs. Falls back to running directly where Web Locks are unavailable;
 * the 409 handling in performRefresh() covers that case.
 */
export const runWithSessionLock = <T>(task: () => Promise<T>): Promise<T> =>
  navigator.locks ? navigator.locks.request(SESSION_LOCK_NAME, task) : task();

export const isAccessTokenExpired = (token: string | null): boolean => {
  if (!token) {
    return true;
  }

  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);

    return !exp || exp - ACCESS_TOKEN_EXPIRY_SKEW_SECONDS <= Date.now() / 1000;
  } catch {
    return true;
  }
};

/**
 * True only when the API has definitively rejected the session (401/403, or nothing to refresh with).
 * Network failures and 5xx are NOT a rejection — the user must not be logged out by a connectivity blip.
 */
export const isSessionRejected = (error: unknown): boolean => {
  if (error instanceof SessionExpiredError) {
    return true;
  }

  const status = (error as AxiosError)?.response?.status;

  return status === 401 || status === 403;
};

const saveTokens = (accessToken: string, refreshToken: string) => {
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('refreshToken', refreshToken);
  axiosIns.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

  // Keep the realtime socket's credentials current so a reconnect doesn't present an expired token
  updateSocketToken(accessToken);
};

const performRefresh = async (failedAccessToken: string | null): Promise<string> => {
  // While this tab waited for the lock, another tab may already have rotated the tokens — reuse them
  const storedAccessToken = localStorage.getItem('accessToken');
  if (storedAccessToken && storedAccessToken !== failedAccessToken && !isAccessTokenExpired(storedAccessToken)) {
    return storedAccessToken;
  }

  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    throw new SessionExpiredError('No refresh token available');
  }

  const refreshRequestConfig = {
    headers: { 'x-hide-loader': true },
    _skipInterceptor: true
  };

  try {
    const { data } = await axiosIns.post('/auth/refresh-tokens', { refreshToken }, refreshRequestConfig);

    saveTokens(data.accessToken, data.refreshToken);

    return data.accessToken;
  } catch (error) {
    // 409 REFRESH_IN_PROGRESS: this session was rotated a moment ago by another tab (only possible
    // without Web Locks). That tab writes the new tokens to localStorage, so pick those up.
    if ((error as AxiosError)?.response?.status === 409) {
      await wait(1000);

      const latestRefreshToken = localStorage.getItem('refreshToken');
      const latestAccessToken = localStorage.getItem('accessToken');

      if (latestAccessToken && latestRefreshToken && latestRefreshToken !== refreshToken) {
        return latestAccessToken;
      }
    }

    throw error;
  }
};

/**
 * Returns a valid access token, refreshing the session if needed. Concurrent callers in this tab share
 * one request, and the cross-tab lock ensures only one tab rotates the refresh token at a time.
 *
 * @param failedAccessToken - the access token that was rejected; if storage already holds a newer one, it is reused
 */
export const refreshSession = (
  failedAccessToken: string | null = localStorage.getItem('accessToken')
): Promise<string> => {
  if (!refreshPromise) {
    refreshPromise = runWithSessionLock(() => performRefresh(failedAccessToken)).finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
};

/**
 * Retries a request that failed with 401 using a refreshed access token. Logs out only when the API
 * rejects the session; a network or server error leaves the session intact.
 */
const retryWithRefreshedToken = async (error: AxiosError) => {
  const originalRequest = error.config as RetriableRequestConfig;

  // Already retried once with a freshly refreshed token and still rejected — the session itself is bad
  if (originalRequest._retry) {
    useAuthStore().logout();

    return Promise.reject(error);
  }

  const failedAccessToken = String(originalRequest.headers?.Authorization || '').replace(/^Bearer\s+/i, '') || null;

  try {
    const newAccessToken = await refreshSession(failedAccessToken);

    originalRequest._retry = true;
    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

    return axiosIns(originalRequest);
  } catch (refreshError) {
    useCommonStore().setLoading(false);

    if (isSessionRejected(refreshError)) {
      useAuthStore().logout();
    } else {
      // toastId de-duplicates: many parallel requests can fail on the same refresh attempt
      toast.error('Unable to reach the server. Please check your connection and try again.', {
        toastId: 'session-refresh-failed'
      });
    }

    return Promise.reject(refreshError);
  }
};

// ℹ️ Add response interceptor to handle 401 response and error response
axiosIns.interceptors.response.use(
  response => {
    useCommonStore().setLoading(false);

    return response.data;
  },
  async error => {
    useCommonStore().setLoading(false);

    const statusCode = error?.response?.data?.statusCode;

    // The API's error envelope uses snake_case `error_code`
    const errorCode = error?.response?.data?.error_code ?? error?.response?.data?.errorCode;
    const originalRequest = error.config;

    // ✅ Skip refresh handling if marked
    if (originalRequest?._skipInterceptor) {
      return Promise.reject(error);
    }

    // ✅ Error codes that require logout
    const logoutErrorCodes = [
      'INVALID_TOKEN',
      'TOKEN_NOT_ACTIVE',
      'NO_TOKEN',
      'MISSING_TENANT_HEADER',
      'TENANT_MISMATCH'
    ];

    // ✅ Check if error code requires logout
    if (errorCode && logoutErrorCodes.includes(errorCode)) {
      const authStore = useAuthStore();

      authStore.logout();
      toast.error(error?.response?.data?.message || 'Session expired. Please login again.');

      return Promise.reject(error);
    }

    // ✅ Handle expired/unauthorized access token (try to refresh)
    // Only attempt refresh/logout for requests that were actually authenticated —
    // a 401 from an unauthenticated call (e.g. a failed login attempt) just means
    // bad credentials, not an expired session, so it shouldn't log the user out.
    const hadAuthHeader = Boolean(originalRequest?.headers?.Authorization);

    if ((statusCode === 401 || errorCode === 'TOKEN_EXPIRED') && hadAuthHeader) {
      return retryWithRefreshedToken(error);
    }

    // ✅ Handle other API errors
    if (axios.isAxiosError(error)) {
      // Skip toast for requests that opted out of error notifications (e.g. dashboard 403 sections)
      if (error.config?.headers?.['x-silent-error'] === 'true') {
        return Promise.reject(error);
      }
      console.log(error?.response);
      toast.error(error?.response?.data?.message || 'Something went wrong');
    }

    return Promise.reject(error);
  }
);

export default axiosIns;
